import { useState, useEffect, useCallback, useMemo } from 'react';
import apiService from '../../../services/api';

export interface Order {
  id: string;
  accessionId: string;
  patientId: string;
  collectionDatetime: string;
  specimenClass: string;
  orderingPhysician: string;
  accessionLocationId: string;
  clinicalHistory: string;
  accessionedBy: string;
  accessionedById: string;
  grossingTechId: string;
  assignedPathologistId: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  amendedFromId?: number;
  amendedAt?: string;
  amendedBy?: string;
  version?: string;
  addendaId?: string;
  orderedBy?: number;
  orderDate?: string;
  createdBy?: string;
  sectionValues?: string;
}

interface PaginatedOrders {
  orders: Order[];
  total: number;
  page: number;
  pageSize: number;
}

export const useOrders = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [pdfGeneratingOrderId, setPdfGeneratingOrderId] = useState<string | null>(null);
  const [pdfSuccess, setPdfSuccess] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>('In Progress');
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});

  const fetchOrders = useCallback(async (pageNum = 0, pageSize = 10, searchTerm?: string, status?: string) => {
    setLoading(true);
    console.log('fetchOrders called with:', { pageNum, pageSize, searchTerm, status });
    try {
      const data: PaginatedOrders = await apiService.getUniqueAccessions(pageNum + 1, pageSize, searchTerm, status);
      console.log('Orders API response:', data);
      setOrders(data.orders || []);
      setTotal(data.total || 0);
    } catch (e) {
      console.error('Error fetching orders:', e);
      setOrders([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStatusCounts = useCallback(async () => {
    try {
      const counts = await apiService.getOrderStatusCounts();
      console.log('Status counts response:', counts);
      setStatusCounts(counts);
    } catch (e) {
      console.error('Error fetching status counts:', e);
      setStatusCounts({});
    }
  }, []);

  const handleChangePage = useCallback((_: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const deleteOrder = useCallback(async (id: string, searchTerm?: string) => {
    if (window.confirm('Delete this order?')) {
      await apiService.deleteOrder(id);
      fetchOrders(page, rowsPerPage, searchTerm, selectedStatus);
      fetchStatusCounts(); // Refresh status counts after deletion
    }
  }, [fetchOrders, fetchStatusCounts, page, rowsPerPage, selectedStatus]);

  const handleStatusChange = useCallback((status: string) => {
    setSelectedStatus(status);
    setPage(0); // Reset to first page when status changes
  }, []);

  const generatePDF = useCallback(async (accessionId: string) => {
    setPdfGenerating(true);
    setPdfGeneratingOrderId(accessionId);
    setPdfSuccess(false);
    
    try {
      console.log('Generating PDF for latest version of accession:', accessionId);
      const response = await apiService.generateOrderPDF(accessionId);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `order_${accessionId}_latest_report.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      console.log('PDF generated and downloaded successfully for latest version');
      
      // Show success notification
      setPdfSuccess(true);
      setTimeout(() => setPdfSuccess(false), 3000); // Auto-hide after 3 seconds
    } catch (error) {
      console.error('Error generating PDF:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Failed to generate PDF report';
      if (error instanceof Error) {
        if (error.message.includes('500')) {
          errorMessage = 'Server error occurred while generating PDF. Please try again later.';
        } else if (error.message.includes('404')) {
          errorMessage = 'Order not found. Please check the accession ID.';
        } else if (error.message.includes('400')) {
          errorMessage = 'Invalid accession ID format.';
        } else if (error.message.includes('403')) {
          errorMessage = 'Order not found. Please refresh the page and try again.';
        } else if (error.message.includes('401') || error.message.includes('403')) {
          errorMessage = 'You are not authorized to generate PDFs for this order.';
        } else {
          errorMessage = `PDF generation failed: ${error.message}`;
        }
      }
      
      alert(errorMessage);
    } finally {
      setPdfGenerating(false);
      setPdfGeneratingOrderId(null);
    }
  }, []);

  // Memoize the return object to prevent unnecessary re-renders
  const result = useMemo(() => ({
    orders,
    page,
    rowsPerPage,
    total,
    loading,
    pdfGenerating,
    pdfGeneratingOrderId,
    pdfSuccess,
    selectedStatus,
    statusCounts,
    fetchOrders,
    fetchStatusCounts,
    handleChangePage,
    handleChangeRowsPerPage,
    handleStatusChange,
    deleteOrder,
    generatePDF,
  }), [
    orders,
    page,
    rowsPerPage,
    total,
    loading,
    pdfGenerating,
    pdfGeneratingOrderId,
    pdfSuccess,
    selectedStatus,
    statusCounts,
    fetchOrders,
    fetchStatusCounts,
    handleChangePage,
    handleChangeRowsPerPage,
    handleStatusChange,
    deleteOrder,
    generatePDF,
  ]);

  // Removed automatic fetching effect to prevent double rendering
  // OrdersList component now handles all fetching logic

  return result;
}; 